/**
 * Policy paragraph editor — shared by the owner's home-page review card and
 * compliance's approval screen.
 *
 * The document is shown as its paragraphs. Any paragraph can be changed
 * (click it), erased, or followed by a new paragraph. The edits are held by
 * the parent as a list of operations:
 *   { index, op: 'replace', text } · { index, op: 'delete' } · { index, op: 'insert_after', text, key }
 * and sent to the server as-is. Indexes are those of the paragraphs given.
 */
import React, { useState } from 'react';
import { diffWords } from './wordDiff';

/** A paragraph's text with its own bold/italic/underline, as in the document. */
function RunText({ p, text, edited }) {
  if (edited || !Array.isArray(p.runs) || !p.runs.length) return text;
  return p.runs.map((r, i) => {
    let node = r.text;
    if (r.u) node = <u key={`u${i}`}>{node}</u>;
    if (r.i) node = <em key={`i${i}`}>{node}</em>;
    if (r.b) node = <strong key={`b${i}`}>{node}</strong>;
    return <React.Fragment key={i}>{node}</React.Fragment>;
  });
}

/** Only the changed words highlighted — the Word tracked-changes reading everyone knows. */
export function DiffText({ before, after, mode }) {
  const d = diffWords(before, after);
  const parts = mode === 'before' ? d.before : d.after;
  return parts.map((t, i) => {
    if (t.same) return <span key={i}>{t.text}</span>;
    return mode === 'before'
      ? <span key={i} className="bg-red-100 text-red-800 line-through decoration-red-400 rounded-[2px]">{t.text}</span>
      : <span key={i} className="bg-emerald-100 text-emerald-900 font-semibold rounded-[2px]">{t.text}</span>;
  });
}

export function editsSummary(ops) {
  const n = { replaced: 0, inserted: 0, deleted: 0 };
  for (const o of ops || []) {
    if (o.op === 'delete') n.deleted += 1;
    else if (o.op === 'insert_after') n.inserted += 1;
    else n.replaced += 1;
  }
  return n;
}

export function editsCount(ops) {
  return (ops || []).length;
}

/** Ops → the request body the server expects (drops empty inserts). */
export function toRequestEdits(ops) {
  return (ops || [])
    .filter((o) => o.op !== 'insert_after' || String(o.text || '').trim())
    .map((o) => (o.op === 'delete' ? { index: o.index, op: 'delete' } : { index: o.index, op: o.op || 'replace', text: o.text }));
}

let keySeq = 0;

export default function PolicyParagraphEditor({ paragraphs, value, onChange, readOnly = false, maxHeight = 'max-h-96', proposals = null, onResolveProposal = null, onDiscuss = null, discussIndex = null, renderDiscuss = null, comments = null, blocks = null, docLook = false, headerLogo = null }) {
  const ops = value || [];
  const [editingIdx, setEditingIdx] = useState(null);      // paragraph index being edited
  const [editingKey, setEditingKey] = useState(null);      // inserted paragraph key being edited

  const replaceOf = (idx) => ops.find((o) => o.op === 'replace' && o.index === idx);
  const isDeleted = (idx) => ops.some((o) => o.op === 'delete' && o.index === idx);
  const insertsAfter = (idx) => ops.filter((o) => o.op === 'insert_after' && o.index === idx);

  const set = (next) => onChange(next);
  const without = (pred) => ops.filter((o) => !pred(o));

  const setReplace = (idx, text, original) => {
    const rest = without((o) => o.op === 'replace' && o.index === idx);
    if (text === original) set(rest);
    else set([...rest, { index: idx, op: 'replace', text }]);
  };
  const undoReplace = (idx) => set(without((o) => o.op === 'replace' && o.index === idx));
  const erase = (idx) => {
    if (isDeleted(idx)) return;
    set([...without((o) => o.op === 'replace' && o.index === idx), { index: idx, op: 'delete' }]);
    setEditingIdx(null);
  };
  const restore = (idx) => set(without((o) => o.op === 'delete' && o.index === idx));
  const addAfter = (idx) => {
    keySeq += 1;
    const key = `ins-${idx}-${keySeq}`;
    set([...ops, { index: idx, op: 'insert_after', text: '', key }]);
    setEditingIdx(null);
    setEditingKey(key);
  };
  const setInsert = (key, text) => set(ops.map((o) => (o.key === key ? { ...o, text } : o)));
  const removeInsert = (key) => { set(without((o) => o.key === key)); if (editingKey === key) setEditingKey(null); };

  const renderPara = (p) => {
    const rep = replaceOf(p.index);
    const deleted = isDeleted(p.index);
    const text = rep ? rep.text : p.text;
    const edited = !!rep;
    const blank = !text.trim() && !edited && !deleted;
    const inserts = insertsAfter(p.index);
    const prop = !readOnly && !rep && !deleted && proposals ? proposals[p.index] : null;

    let node;
    if (prop) {
      node = (
        <div key={`p-${p.index}`} className="rounded-lg ring-1 ring-sky-200 bg-white p-2 space-y-1.5">
          <p className="text-[10px] font-semibold uppercase tracking-[0.05em] text-sky-700">Suggested change{prop.heading ? ` · ${prop.heading}` : ''}</p>
          <p className="text-[13px] leading-relaxed text-slate-700 bg-red-50/50 rounded px-2 py-1"><DiffText before={p.text} after={prop.after} mode="before" /></p>
          <p className="text-[13px] leading-relaxed text-slate-800 bg-emerald-50/60 rounded px-2 py-1"><DiffText before={p.text} after={prop.after} mode="after" /></p>
          {prop.citation && (
            <p className="text-[11.5px] text-slate-500 border-l-2 border-slate-300 pl-2 italic">
              &ldquo;{String(prop.citation.quote || '').slice(0, 260)}&rdquo; — {prop.citation.source === 'state_contract' ? (prop.citation.label || 'State Medicaid contract') : prop.citation.source === 'requirement' ? (prop.citation.label || 'Requirement on file') : (prop.citation.citation || prop.citation.label || 'source on file')}
            </p>
          )}
          <div className="flex gap-3 flex-wrap pt-0.5">
            <button type="button" onClick={() => { setReplace(p.index, prop.after, p.text); onResolveProposal?.(p.index, 'accepted', prop, p.text); }}
              className="text-[12px] font-semibold text-emerald-700 hover:text-emerald-900">Use this wording</button>
            <button type="button" onClick={() => { setReplace(p.index, prop.after, p.text); onResolveProposal?.(p.index, 'edited', prop, p.text); setEditingIdx(p.index); }}
              className="text-[12px] font-semibold text-orange-700 hover:text-orange-900">Edit it first</button>
            {onDiscuss && (
              <button type="button" onClick={() => onDiscuss(p.index, '')}
                className="text-[12px] font-semibold text-sky-700 hover:text-sky-900">Ask</button>
            )}
            <button type="button" onClick={() => onResolveProposal?.(p.index, 'dismissed', prop, p.text)}
              className="text-[12px] text-slate-500 hover:text-slate-700">Dismiss</button>
          </div>
        </div>
      );
    } else if (deleted) {
      node = (
        <div key={`p-${p.index}`} className="group rounded-lg px-2 py-1 bg-red-50 ring-1 ring-red-200 flex items-start justify-between gap-2">
          <p className="text-[13px] leading-relaxed text-red-800 line-through">{p.text || <span className="text-red-300">(empty)</span>}</p>
          {!readOnly && (
            <button type="button" onClick={() => restore(p.index)} className="flex-shrink-0 text-[11px] font-semibold text-red-700 hover:text-red-900">Restore</button>
          )}
        </div>
      );
    } else if (!readOnly && editingIdx === p.index) {
      node = (
        <div key={`p-${p.index}`} className="rounded-lg ring-2 ring-orange-300 bg-white p-2">
          <textarea autoFocus rows={Math.min(10, Math.max(2, Math.ceil((text || '').length / 90)))}
            value={text}
            onChange={(e) => setReplace(p.index, e.target.value, p.text)}
            className="w-full text-[13px] text-slate-800 outline-none resize-y leading-relaxed" />
          <div className="flex gap-3 mt-1 flex-wrap">
            <button type="button" onClick={() => setEditingIdx(null)} className="text-[12px] font-semibold text-orange-700">Done</button>
            <button type="button" onClick={() => { undoReplace(p.index); setEditingIdx(null); }} className="text-[12px] text-slate-500">Undo this edit</button>
            <button type="button" onClick={() => erase(p.index)} className="text-[12px] text-red-600">Erase this paragraph</button>
          </div>
        </div>
      );
    } else if (blank) {
      node = (
        <div key={`p-${p.index}`} className="group h-3 relative">
          {!readOnly && (
            <button type="button" onClick={() => addAfter(p.index)} title="Add a paragraph here"
              className="absolute right-1 -top-1 hidden group-hover:inline text-[10px] font-semibold text-slate-400 hover:text-orange-700">+ Add paragraph</button>
          )}
        </div>
      );
    } else {
      node = (
        <div key={`p-${p.index}`} className="group relative">
          <p onClick={() => {
              if (readOnly) return;
              const sel = window.getSelection?.();
              if (sel && !sel.isCollapsed) return; // selected words stay selected — Discuss picks them up
              setEditingIdx(p.index);
            }}
            title={readOnly ? undefined : 'Click to edit. Select words first to keep them selected for Discuss.'}
            className={`text-[13px] leading-relaxed rounded-lg px-2 py-1 transition ${readOnly ? '' : 'cursor-text'} ${
              p.heading ? `font-semibold text-slate-900 mt-2 ${p.headingLevel === 1 ? 'text-[17px]' : p.headingLevel === 2 ? 'text-[15px]' : ''}` : 'text-slate-700'
            } ${p.align === 'center' ? 'text-center' : p.align === 'right' ? 'text-right' : ''} ${edited ? 'bg-amber-50 ring-1 ring-amber-200' : readOnly ? '' : 'hover:bg-white hover:ring-1 hover:ring-slate-200'}`}>
            <RunText p={p} text={text} edited={edited} />
            {Array.isArray(p.images) && p.images.map((src, ii) => (
              <img key={ii} src={src} alt="" className="max-w-full my-2 block" />
            ))}
            {edited && <span className="ml-2 text-[10px] font-semibold uppercase text-amber-700">edited</span>}
          </p>
          {!readOnly && (
            <div className="absolute right-1 top-0.5 hidden group-hover:flex gap-2 bg-white/90 rounded-md px-1.5 py-0.5 ring-1 ring-slate-200">
              {onDiscuss && (
                <button type="button" onMouseDown={(e) => e.preventDefault()}
                  onClick={() => onDiscuss(p.index, String(window.getSelection?.() || '').trim())}
                  title="Teach the agent about this text, or ask compliance. Select a word or sentence first to point at it exactly."
                  className="text-[10px] font-semibold text-sky-600 hover:text-sky-800">Discuss</button>
              )}
              <button type="button" onClick={() => setEditingIdx(p.index)} className="text-[10px] font-semibold text-slate-500 hover:text-orange-700">Edit</button>
              <button type="button" onClick={() => addAfter(p.index)} className="text-[10px] font-semibold text-slate-500 hover:text-orange-700">Add below</button>
              <button type="button" onClick={() => erase(p.index)} className="text-[10px] font-semibold text-slate-500 hover:text-red-700">Erase</button>
            </div>
          )}
        </div>
      );
    }

    const insertedNodes = inserts.map((ins) => (
      <div key={ins.key} className={`rounded-lg bg-emerald-50 ring-1 ${editingKey === ins.key ? 'ring-2 ring-orange-300 bg-white' : 'ring-emerald-200'} p-2`}>
        {readOnly || editingKey !== ins.key ? (
          <div className="flex items-start justify-between gap-2">
            <p onClick={() => !readOnly && setEditingKey(ins.key)} className={`text-[13px] leading-relaxed text-slate-800 ${readOnly ? '' : 'cursor-text'}`}>
              {ins.text || <span className="text-slate-400">(new paragraph — click to write)</span>}
              <span className="ml-2 text-[10px] font-semibold uppercase text-emerald-700">new</span>
            </p>
            {!readOnly && <button type="button" onClick={() => removeInsert(ins.key)} className="flex-shrink-0 text-[11px] text-slate-500 hover:text-red-700">Remove</button>}
          </div>
        ) : (
          <>
            <textarea autoFocus rows={Math.min(10, Math.max(2, Math.ceil((ins.text || '').length / 90)))}
              value={ins.text} placeholder="Write the new paragraph"
              onChange={(e) => setInsert(ins.key, e.target.value)}
              className="w-full text-[13px] text-slate-800 outline-none resize-y leading-relaxed bg-transparent" />
            <div className="flex gap-3 mt-1">
              <button type="button" onClick={() => setEditingKey(null)} className="text-[12px] font-semibold text-orange-700">Done</button>
              <button type="button" onClick={() => removeInsert(ins.key)} className="text-[12px] text-slate-500">Remove</button>
            </div>
          </>
        )}
      </div>
    ));

    const discussNode = discussIndex === p.index && typeof renderDiscuss === 'function'
      ? <div key={`d-${p.index}`}>{renderDiscuss()}</div> : null;
    // Review notes live in the body, under the paragraph they are about.
    const TONE = { amber: 'border-amber-400 bg-amber-50/70', slate: 'border-slate-300 bg-slate-50', red: 'border-red-400 bg-red-50/70', emerald: 'border-emerald-400 bg-emerald-50/70' };
    const commentNodes = (comments && Array.isArray(comments[p.index]) ? comments[p.index] : []).map((c, ci) => (
      <div key={`c-${p.index}-${ci}`} className={`ml-3 my-1 rounded-r-lg border-l-[3px] px-3 py-2 ${TONE[c.tone] || TONE.amber}`}>
        <p className="text-[10.5px] font-semibold uppercase tracking-[0.05em] text-slate-600">{c.label}{c.heading && c.heading !== c.label ? ` · ${c.heading}` : ''}{c.by ? ` · ${c.by}` : ''}</p>
        {c.note && <p className="text-[12.5px] leading-relaxed text-slate-800 mt-0.5">{c.note}</p>}
        {c.caveat && <p className="text-[11.5px] text-slate-500 mt-0.5">{c.caveat}</p>}
        {(c.citations || []).slice(0, 3).map((q, qi) => (
          <p key={qi} className="text-[11.5px] text-slate-600 border-l-2 border-slate-300 pl-2 mt-1 italic">
            &ldquo;{String(q.quote || '').slice(0, 260)}&rdquo;{q.label || q.citation ? ` — ${q.source === 'state_contract' ? (q.label || 'State Medicaid contract') : (q.label || q.citation)}` : ''}
          </p>
        ))}
        {typeof c.render === 'function' ? c.render() : null}
      </div>
    ));
    return [node, ...commentNodes, discussNode, ...insertedNodes].filter(Boolean);
  };

  const byIdx = new Map((paragraphs || []).map((p) => [p.index, p]));
  // With the document's structure on hand, render the REAL document — tables
  // as tables — with every interaction still anchored per paragraph.
  const content = Array.isArray(blocks) && blocks.length
    ? blocks.map((b, bi) => {
        if (b.type === 'table') {
          return (
            <table key={`tbl-${bi}`} className="w-full border-collapse my-3">
              <tbody>
                {(b.rows || []).map((cells, ri) => (
                  <tr key={ri}>
                    {(cells || []).map((idxs, ci) => (
                      <td key={ci} className="border border-slate-300 px-2.5 py-1 align-top">
                        {(idxs || []).map((ix) => (byIdx.has(ix) ? renderPara(byIdx.get(ix)) : null))}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          );
        }
        return byIdx.has(b.index) ? renderPara(byIdx.get(b.index)) : null;
      })
    : (paragraphs || []).map((p) => renderPara(p));

  return docLook ? (
    <div className="mx-auto w-full max-w-[880px] bg-white shadow-lg ring-1 ring-slate-200 rounded-sm px-8 sm:px-14 py-10 space-y-1"
      style={{ fontFamily: "Calibri, 'Segoe UI', system-ui, sans-serif" }}>
      {headerLogo && <img src={headerLogo} alt="" className="max-h-16 mb-6" />}
      {content}
    </div>
  ) : (
    <div className={`${maxHeight} overflow-y-auto rounded-xl ring-1 ring-slate-200 bg-slate-50/60 p-4 space-y-1`}>
      {content}
    </div>
  );
}
